/// <amd-module name="TFS/Admin/Common/Controls/SelectUserPanel/SelectUserPanel" />
import * as React from "react";
import { IPanelSelectChange } from "TFS/Admin/Common/Contracts/SelectUserPanel";
import { ISelectUserPanelProps } from "TFS/Admin/Common/Controls/SelectUserPanel/SelectUserPanel.Props";
import { ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { VssComponent } from "VSS/Platform/Layout";
import { IdentityPickerDropdown, IIdentity, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";

import AdminResources = require("TFS/Admin/Common/Controls/Resources");

const reasonMaxLength = 256;

interface ISelectUserPanelState {
    hasError: boolean;
    errorComponent?: JSX.Element;
    enableActionButton: boolean;
    errorTextType?: MessageBarSeverity;
    updateInProgress: boolean;
}

export class SelectUserPanel extends VssComponent<ISelectUserPanelProps, ISelectUserPanelState> {
    private _pickerProvider: IPeoplePickerProvider;
    private _selectedUser: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
    private reason: ObservableValue<string> = new ObservableValue<string>("");

    constructor(props: ISelectUserPanelProps) {
        super(props);
        this.state = {
            hasError: false,
            errorTextType: MessageBarSeverity.Error,
            enableActionButton: false,
            updateInProgress: false
        };

        this._pickerProvider = new PeoplePickerProvider(this.props.pageContext, {
            identityTypes: this.props.identityTypesToLookup || ["user"],
            options: {
                MinResults: 5,
                MaxResults: 20
            }
        });
    }

    public render(): JSX.Element {
        return (
            <Panel
                showSeparator={false}
                onDismiss={this.props.onCancel}
                titleProps={{ text: this.props.panelTitle }}
                description={this.props.panelDescription}
                defaultActiveElement=".select-user-picker input"
                footerButtonProps={[
                    {
                        text: AdminResources.Cancel,
                        onClick: () => {
                            this.props.onCancel();
                        }
                    },
                    {
                        text: this.props.actionButtonText,
                        primary: true,
                        onClick: this._onClick,
                        disabled:
                            !this.state.enableActionButton ||
                            this.state.updateInProgress ||
                            (this.props.requestReason && this.reason.value.length === 0)
                    }
                ]}
            >
                <div className="flex-column select-user">
                    <div className="flex-column">
                        <IdentityPickerDropdown
                            className="select-user-picker"
                            pickerProvider={this._pickerProvider}
                            onChange={this._onUserChangeValidation}
                            suggestionsContainerAriaLabel={AdminResources.suggesstionsContainerAriaLabel}
                            value={this._selectedUser}
                            placeholder={this.props.selectUserPlaceHolderText}
                            editPlaceholder={this.props.selectUserEditPlaceHolderText}
                        />
                        {this.state.hasError ? (
                            <MessageBar className="user-error" severity={this.state.errorTextType!}>
                                {this.state.errorComponent}
                            </MessageBar>
                        ) : null}
                        {this.props.requestReason ? (
                            <>
                                <TextField
                                    className="margin-top-16 change-owner-reason"
                                    ariaLabel={AdminResources.reasonDescription}
                                    value={this.reason}
                                    multiline={true}
                                    placeholder={AdminResources.reasonDescription}
                                    onChange={(event, value) => {
                                        this.reason.value = value;
                                        this.setState({ enableActionButton: this.state.enableActionButton });
                                    }}
                                    maxLength={reasonMaxLength}
                                />
                                <div className="margin-top-8">{AdminResources.reasonEmailNotice}</div>
                            </>
                        ) : null}
                    </div>
                    {this.state.updateInProgress ? <Spinner className="select-user-spinner" size={SpinnerSize.large} /> : null}
                </div>
            </Panel>
        );
    }

    private _onUserChangeValidation = (item?: IIdentity): boolean => {
        if (item) {
            const errorMessage: IPanelSelectChange = this.props.onUserChange(item);
            if (errorMessage.enableActionButton) {
                this._selectedUser.value = item;
            }
            if (errorMessage.hasError) {
                const errorTextType = (errorMessage.errorTextType as unknown) as MessageBarSeverity;
                this.setState({
                    hasError: errorMessage.hasError,
                    errorComponent: errorMessage.errorComponent,
                    errorTextType: errorTextType ? errorTextType : MessageBarSeverity.Error,
                    enableActionButton: errorMessage.enableActionButton
                });
            } else {
                this.setState({ hasError: false, errorComponent: <React.Fragment />, enableActionButton: errorMessage.enableActionButton });
            }
            return true;
        } else {
            this._selectedUser.value = item;
            this.setState({ hasError: false, enableActionButton: false });
            return false;
        }
    };

    private _onClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        this.setState({ updateInProgress: true });
        this.props.onSelectUser(this._selectedUser.value, this.reason.value);
    };
}

VssComponent.register("ms.vss-admin-web.select-user-panel", SelectUserPanel);
